/**
 * 旅行版MBTI 質問セット「質問３」
 * 正本: https://docs.google.com/spreadsheets/d/1FN0ULYHp_ZA1ZKqcOo6libNm9jicPzytOIxldGoaDVI
 * シート: 質問３ (gid=603140462)
 */
export type DiagnosisAxis = 'PvsS' | 'RvsA' | 'LvsC' | 'EvsZ';

export interface DiagnosisQuestion {
  id: number;
  question: string;
  axis: DiagnosisAxis;
  reverse: boolean;
}

/** 5段階（Choice1〜5）の表示ラベル */
export const diagnosisScaleLabels = [
  'とても当てはまる',
  '当てはまる',
  'どちらともいえない',
  'あまり当てはまらない',
  'まったく当てはまらない',
] as const;

/** Score1〜5 に対応（とても当てはまる=+2 〜 まったく当てはまらない=-2） */
export const diagnosisScoreMap = [2, 1, 0, -1, -2] as const;

export const diagnosisQuestionsV3: DiagnosisQuestion[] = [
  { id: 1, question: '旅行前に、行きたい場所を地図や保存にどんどんストックしていく', axis: 'PvsS', reverse: false },
  { id: 2, question: 'ホテル・移動・人気スポットは、早めに押さえておくと安心する', axis: 'PvsS', reverse: false },
  { id: 3, question: '現地で「ここ気になる！」で予定をサクッと変えるのが好き', axis: 'PvsS', reverse: true },
  { id: 4, question: '想定外の出来事が起きても「それも思い出！」って楽しめる', axis: 'PvsS', reverse: true },
  { id: 5, question: '旅には「何もしない時間（カフェ/散歩/ぼーっと）」が必要', axis: 'RvsA', reverse: true },
  { id: 6, question: 'せっかくなら1日でいろいろ回りたくなる', axis: 'RvsA', reverse: false },
  { id: 7, question: '移動が多いほど「旅行した〜！」って満足しやすい', axis: 'RvsA', reverse: false },
  { id: 8, question: '朝はゆっくりスタートしたい（起きてからもまったりしたい）', axis: 'RvsA', reverse: true },
  { id: 9, question: '旅の満足度は「何食べたか」で決まることが多い', axis: 'LvsC', reverse: false },
  { id: 10, question: '初めてのお店でも、雰囲気が良さそうなら入れる', axis: 'LvsC', reverse: false },
  { id: 11, question: '食で失敗したくなくて、結局“安心できる選択”に寄りがち', axis: 'LvsC', reverse: true },
  { id: 12, question: 'コンビニより、スーパー・市場・屋台のほうがテンション上がる', axis: 'LvsC', reverse: false },
  { id: 13, question: '旅には「初めての体験」を1つは入れたい（体験/アクティビティ/挑戦）', axis: 'EvsZ', reverse: false },
  { id: 14, question: '旅の目的は「癒し・整う（温泉/サウナ/静けさ）」が強い', axis: 'EvsZ', reverse: true },
  { id: 15, question: '少し不便でも、非日常っぽい場所（秘境/絶景/自然）に惹かれる', axis: 'EvsZ', reverse: false },
  { id: 16, question: '予定を詰めるより、景色のいい場所でのんびり過ごしたい', axis: 'EvsZ', reverse: true },
];

import { CODE_TO_PAGE_PATH } from './diagnosisResults/registry';

/** 4軸コード（例: PALZ）→ 結果ページパス（正本: diagnosisResults/registry.ts） */
export const diagnosisResultPageMap: Record<string, string> = { ...CODE_TO_PAGE_PATH };

export function calculateDiagnosisResultPath(
  answers: Record<number, number>,
): string {
  const axisScores: Record<DiagnosisAxis, number> = {
    PvsS: 0,
    RvsA: 0,
    LvsC: 0,
    EvsZ: 0,
  };

  diagnosisQuestionsV3.forEach((q) => {
    const choiceIndex = answers[q.id];
    if (choiceIndex === undefined) return;

    const raw = diagnosisScoreMap[choiceIndex];
    axisScores[q.axis] += q.reverse ? -raw : raw;
  });

  const axis1 = axisScores.PvsS > 0 ? 'P' : 'S';
  const axis2 = axisScores.RvsA > 0 ? 'A' : 'R';
  const axis3 = axisScores.LvsC > 0 ? 'L' : 'C';
  const axis4 = axisScores.EvsZ > 0 ? 'E' : 'Z';

  const resultType = `${axis1}${axis2}${axis3}${axis4}`;
  return diagnosisResultPageMap[resultType] || '/diagnosis-result2';
}
